import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Card, CardContent, Grid, IconButton, Typography, Box } from '@mui/material';
import NumberFormatTextField from '../../../../../components/NumberFormatTextField/NumberFormatTextField';
import { addThousandSeparators, countDecimals, roundNumber } from '../../../../../utils/formatNumber';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import './InputPanel.scss';

const LoanEditButtonStyle = {
  padding: '0px 5px',
  position: 'absolute',
  // Labels were lifted 8px (see InputPanel.scss); keep the edit pencil aligned with them.
  top: -8,
  right: 0,
  zIndex: 1
};

const LoadToggleButtonStyle = {
  minHeight: 'unset',
  minWidth: 'unset',
  padding: 2,
  lineHeight: 1
};

const LoanAmountTypoStyle = {
  whiteSpace: 'pre'
};

const InputPanel = ({
  intl,
  principal,
  base,
  handleChange,
  loanPercentage,
  firstYearBonus,
  openLoanCalculatorDialog,
  openBonusCalculatorDialog,
  template,
  disableLoan,
  onToggleDisableLoan,
  isCurrencyEnabled,
  currency
}) => {
  const _handleChange = isCurrencyEnabled ? undefined : handleChange;
  const formattedLoanPercentage = roundNumber(loanPercentage, 1);
  const loan = (principal ?? 0) - (base ?? 0) - (firstYearBonus ?? 0);
  const onClickToggleDisableLoanButton = () => onToggleDisableLoan();

  return (
    <Card className="pfa-input-panel pfa-card pf-card-container">
      <CardContent style={{ paddingTop: 22, paddingBottom: 0 }}>
        <Grid container spacing={1}>
          <Grid size={{ xs: 6, md: 3 }} className="input-control">
            <NumberFormatTextField
              disabled={isCurrencyEnabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ShieldOutlinedIcon style={{ fontSize: 16, color: '#665EFF' }} />
                  <span>{intl.formatMessage({ id: 'pfa-principal' })}</span>
                </Box>
              }
              name="principal"
              value={principal}
              onChange={_handleChange}
              inputProps={{
                allowNegative: false,
                prefix: '$',
                decimalScale: 0
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} className="input-control loan-ratio-control">
            <NumberFormatTextField
              disabled={isCurrencyEnabled}
              label={
                <Grid
                  container
                  style={{ marginTop: -10, width: '100%' }}
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  <Grid
                    style={{
                      marginRight: 6,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <PercentOutlinedIcon style={{ fontSize: 16, color: '#FFA427' }} />
                  </Grid>
                  <Grid>
                    <Button
                      disabled={isCurrencyEnabled}
                      variant="outlined"
                      color={disableLoan ? 'inherit' : 'primary'}
                      style={LoadToggleButtonStyle}
                      onClick={onClickToggleDisableLoanButton}
                    >
                      <FormattedMessage
                        id="pfa-loan-percentage"
                        values={{
                          ratio: (
                            <span className="pfa-loan-ratio-text">
                              {intl.formatMessage({
                                id: 'pfa-loan-ratio-suffix'
                              })}
                            </span>
                          )
                        }}
                      />
                    </Button>
                  </Grid>
                  <Grid style={{ ...LoanAmountTypoStyle, marginLeft: 8 }}>
                    <Typography style={{ fontSize: 14 }}>
                      {intl.formatMessage({
                        id: 'pfa-loan-percentage-amount'
                      })}
                    </Typography>
                  </Grid>
                  {!template && (
                    <Grid style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                      <IconButton
                        disabled={isCurrencyEnabled}
                        onClick={openLoanCalculatorDialog}
                        style={{ padding: 0 }}
                        size="small"
                      >
                        <EditIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              }
              name="loanPercentage"
              value={formattedLoanPercentage}
              inputProps={{
                allowNegative: false,
                suffix: `%`,
                decimalScale: 1,
                isAllowed: values => {
                  const { formattedValue, value } = values;
                  return principal && (formattedValue === '' || (value >= 0 && value <= 100));
                },
                style: disableLoan
                  ? {
                      visibility: 'hidden'
                    }
                  : undefined
              }}
              onChange={_handleChange}
              endAdornment={
                !disableLoan ? (
                  <div style={{ marginLeft: 8 }}>{loan ? `($${addThousandSeparators(loan.toFixed(0))})` : ''}</div>
                ) : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} className="input-control">
            {!template && (
              <IconButton
                disabled={isCurrencyEnabled}
                onClick={openBonusCalculatorDialog}
                style={LoanEditButtonStyle}
                size="large"
              >
                <EditIcon style={{ fontSize: 16, opacity: 0.1 }} />
              </IconButton>
            )}
            <NumberFormatTextField
              disabled={isCurrencyEnabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CardGiftcardOutlinedIcon style={{ fontSize: 16, color: '#0FAF3F' }} />
                  <span>{intl.formatMessage({ id: 'pfa-bonus' })}</span>
                </Box>
              }
              name="firstYearBonus"
              value={firstYearBonus}
              onChange={_handleChange}
              inputProps={{
                allowNegative: false,
                prefix: '$',
                decimalScale: 0,
                isAllowed: values => {
                  const { formattedValue, value } = values;
                  return (
                    principal &&
                    (formattedValue === '' ||
                      (value >= 0 && value <= principal * (1 - (disableLoan ? 0 : loanPercentage ?? 0) / 100)))
                  );
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }} className="input-control">
            <NumberFormatTextField
              disabled={isCurrencyEnabled}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <AccountBalanceWalletOutlinedIcon style={{ fontSize: 16, color: '#3966f8' }} />
                  <span>{intl.formatMessage({ id: 'pfa-base' })}</span>
                </Box>
              }
              name="base"
              value={base}
              onChange={_handleChange}
              inputProps={{
                allowNegative: false,
                prefix: '$',
                decimalScale: 0,
                isAllowed: values => {
                  const { formattedValue, value } = values;
                  return (
                    principal && (formattedValue === '' || (value >= 0 && value <= principal - (firstYearBonus ?? 0)))
                  );
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default InputPanel;
